import {
  ActionButton,
  BusinessPicker,
  OfficeHeader,
  Paginator,
  RegularBusinessPicker,
  RegularInsurerPicker,
  RegularSelectInput,
  Show,
  TableComponent,
  YearHeader,
} from "components";
import { FC, useMemo } from "react";
import {
  Payment_status,
  PublishedStatus,
} from "_graphql/__generated__/globalTypes";
import {
  extractVehicleNumberFromDetails,
  useUrlState,
  withPermissions,
} from "utils";
import { useNavigate, useSearch } from "react-location";

import { Action } from "components/buttons/action-button";
import AttachSupportingDocumentsContainer from "pages/create-slip/attach-supporting-documents";
import { CheckBadgeIcon } from "@heroicons/react/24/outline";
import { LocationGenerics } from "router/location";
import { Offers_rows } from "_graphql/queries/__generated__/Offers";
import RemoveContainer from "./remove";
import ReopenContainer from "./reopen";
import SendEmailsContainer from "./send-documents";
import SendMessageContainer from "pages/place-it-offers/message-thread";
import ViewNotesContainer from "./view";
import YearPicker from "layouts/calendar-wrapper/year-picker";
import clsx from "clsx";
import numeral from "numeral";
import { useFormik } from "formik";
import { useOffers } from "hooks/data/use-offers";
import { usePagination } from "hooks";
import { useUpdateBubble } from "hooks/data/use-update-bubble";
import { columns } from "./columns";

type Props = {};

const CreateClosingPage: FC<Props> = () => {
  const [modal, setModal] = useUrlState("modal");
  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const { offset, limit, setPage, search } = usePagination();
  const { updateBubble } = useUpdateBubble();

  const form = useFormik<any>({
    initialValues: {
      insurer: "",
      payment_status: "",
      business: "",
    },
    onSubmit: () => {},
  });

  const filter = useMemo(
    () => ({
      page: searchParams.page || 1,
      pageSize: searchParams.pageSize || 10,
      search: (search || "").toString(),
      args: {
        status: ["CLOSED"],
        insurer_id: form.values.insurer || "",
        year: searchParams.year?.toString() || "",
        offer_from: searchParams.offer_from || "",
        class_of_business_id: form.values.business || "",
        payment_status: form.values.payment_status
          ? [form.values.payment_status]
          : [],
        claim_status: [],
      },
    }),
    [
      form.values.insurer,
      form.values.business,
      form.values.payment_status,
      searchParams.page,
      searchParams.pageSize,
      searchParams.year,
      search,
      searchParams.offer_from,
    ]
  );

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  const expandItem = (id: string) => {
    navigate({
      to: `./${id}/credit-notes`,
    });
  };

  const updateThreadBubble = (ids: string[]) => {
    updateBubble({
      variables: {
        ids,
      },
    })
      .then(() => {})
      .catch((err) => {});
  };

  const { offers, loading, refetch, count } = useOffers(filter);

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <YearHeader />
      {/* {JSON.stringify(form.values.insurer, null, 2)} */}
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            title={"offers"}
            hasSearch
            loading={loading}
            columns={columns.concat([
              {
                name: "Actions",
                accessor: "actions",
                render: (value) => (
                  <>
                    <ActionButton
                      action="view"
                      tooltip="View Documents & History"
                      onClick={dispatchAction(
                        value.offer_id +
                          ":" +
                          value?.offer_from +
                          ":" +
                          value?.place_it_id,
                        "view"
                      )}
                    />
                    {value.offer_from !== "INTERNAL" && (
                      <ActionButton
                        action="chat"
                        tooltip="View Message Thread"
                        onClick={dispatchAction(
                          value?.place_it_id || "",
                          "chat"
                        )}
                        count={
                          value.thread_notification?.filter(
                            (item) => item?.status !== "READ"
                          ).length
                        }
                        callBack={() =>
                          updateThreadBubble(
                            value.thread_notification?.map(
                              (item) => item?.id || ""
                            ) || []
                          )
                        }
                      />
                    )}
                    {withPermissions([
                      "*:*",
                      "offers:manage",
                      "offers:*",
                      "offers:send",
                    ])(
                      <ActionButton
                        action="send"
                        tooltip="Send Notes"
                        onClick={dispatchAction(value?.offer_id, "send")}
                      />
                    )}
                    <ActionButton
                      action="goto"
                      tooltip="View Credit Notes"
                      onClick={() => expandItem(value?.offer_id)}
                    />
                    <Show
                      if={
                        value?.offer_from === "INTERNAL" ||
                        (value?.offer_from !== "INTERNAL" &&
                          value?.version === "V1")
                      }
                    >
                      {withPermissions(["*:*", "offers:reinstate"])(
                        <ActionButton
                          action="reject"
                          tooltip="Reopen Offer"
                          onClick={dispatchAction(value?.offer_id, "reject")}
                        />
                      )}
                    </Show>
                    <Show
                      if={
                        value?.offer_from === "INTERNAL" ||
                        (value?.offer_from !== "INTERNAL" &&
                          value?.version === "V1")
                      }
                    >
                      {withPermissions([
                        "*:*",
                        "offers:*",
                        "offers:manage",
                        "offers:update",
                      ])(
                        <ActionButton
                          action="attach"
                          tooltip="Add Supporting Documents"
                          onClick={dispatchAction(value?.offer_id, "import")}
                        />
                      )}
                    </Show>
                    <Show
                      if={
                        value?.offer_from === "INTERNAL" ||
                        (value?.offer_from !== "INTERNAL" &&
                          value?.version === "V1")
                      }
                    >
                      {withPermissions([
                        "*:*",
                        "offers:*",
                        "offers:manage",
                        "offers:delete",
                      ])(
                        <ActionButton
                          action="remove"
                          tooltip="Delete Offer"
                          onClick={dispatchAction(value?.offer_id, "remove")}
                        />
                      )}
                    </Show>
                  </>
                ),
              },
            ])}
            data={offers}
            refetch={refetch}
            renderHeaderItems={() => (
              <div className="flex items-center space-x-2">
                <RegularSelectInput
                  id="offer_from"
                  hideLabel
                  label="Offer From"
                  placeholder="Filter by Offer From"
                  options={["INTERNAL", "PLACEIT"].map((tab) => ({
                    value: tab,
                    label: tab,
                  }))}
                  values={{ offer_from: searchParams?.offer_from || "" }}
                  handleChange={(evt: any) => {
                    navigate({
                      search: (old) => ({
                        ...old,
                        offer_from: evt.target.value,
                        page: 1,
                      }),
                    });
                  }}
                />
                <RegularBusinessPicker
                  id="business"
                  label="Business"
                  rawId
                  labelHidden
                  {...form}
                />

                <RegularInsurerPicker
                  placeholder="Filter by Cedant"
                  id={"insurer"}
                  rawId
                  labelHidden
                  label={""}
                  {...form}
                />
                <RegularSelectInput
                  id="payment_status"
                  label="Payment Status"
                  hideLabel
                  options={[
                    {
                      label: "Paid",
                      value: Payment_status.PAID,
                    },
                    {
                      label: "Partpayment",
                      value: Payment_status.PARTPAYMENT,
                    },
                    {
                      label: "Unpaid",
                      value: Payment_status.UNPAID,
                    },
                  ]}
                  {...form}
                />
              </div>
            )}
            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                totalItems={count || 0}
                currentSize={offers.length}
                setOffset={setPage}
              />
            )}
          />
        </div>
      </div>
      {searchParams?.id && modal === "view" && (
        <ViewNotesContainer
          open={modal === "view"}
          setOpen={(val: boolean) => setModal(val ? "view" : undefined)}
        />
      )}
      {searchParams?.id && modal === "remove" && (
        <RemoveContainer
          open={modal === "remove"}
          setOpen={(val: boolean) => setModal(val ? "remove" : undefined)}
        />
      )}
      {searchParams?.id && modal === "reject" && (
        <ReopenContainer
          open={modal === "reject"}
          setOpen={(val: boolean) => setModal(val ? "reject" : undefined)}
        />
      )}
      {searchParams?.id && modal === "send" && (
        <SendEmailsContainer
          open={modal === "send"}
          setOpen={(val: boolean) => setModal(val ? "send" : undefined)}
        />
      )}
      {searchParams?.id && modal === "chat" && (
        <SendMessageContainer
          open={modal === "chat"}
          setOpen={(val: boolean) => setModal(val ? "chat" : undefined)}
        />
      )}
      {searchParams?.id && modal === "import" && (
        <AttachSupportingDocumentsContainer
          open={modal === "import"}
          setOpen={(val: boolean) => setModal(val ? "import" : undefined)}
        />
      )}
    </main>
  );
};

export default CreateClosingPage;
